import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import Image from 'next/image';

import TemplateContainer from '@app/components/atoms/TemplateContainer';
import Head from '@app/components/molecules/Head';
import PricingComponent from '@app/components/atoms/PricingComponent';
import WaitlistFormModal from '@app/components/molecules/WaitlistFormModal';

import { useQuery } from '@app/hooks';
import { CREATE_SUBSCRIPTION } from '@app/queries/createSubscription';
import { GET_USER } from '@app/queries/getUser';
import { userUpdate } from '@app/store/user';
import { Auth } from 'aws-amplify';

import * as Styled from './styles';
import CompassLogo from '@public/images/other-logos/compass-logo.png';
import SideLogo from '@public/images/other-logos/side-logo.png';
import SotherbysLogo from '@public/images/other-logos/sotherbys-logo.png';
import ColdwellLogo from '@public/images/other-logos/coldwell-logo.png';

interface MyWindow extends Window {
  Intercom?: (event: string, options?: unknown | null) => void;
}

const Pricing = () => {
  const w = window as MyWindow;

  const router = useRouter();
  const userData: any = useSelector<any>((state) => state.user);
  const dispatch = useDispatch();

  const [proType, setProType] = useState('monthly');
  const [eliteType, setEliteType] = useState('yearly');
  const [waitlistModal, setWaitlistModal] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  const isLoggedIn = Boolean(Object.keys(userData).length) || hasValidSession;
  const subscriptionValid = !!(
    userData?.subscription?.status === 'trialing' ||
    userData?.subscription?.status === 'active'
  );

  const userSubscriptionType = userData?.subscription?.subscriptionType;

  const createSubscription = useQuery({
    query: CREATE_SUBSCRIPTION,
    useIsLoading: true,
    onResult: ({ createSubscription }: any) => {
      window.location.href = createSubscription;
    },
    onFail: (err) => Sentry.captureException(err),
  });

  const getUserQuery = useQuery({
    query: GET_USER,
    onResult: ({ getUser }: any) => {
      dispatch(userUpdate(getUser));

      // Check if agent needs to complete profile
      if (getUser?.userType === 'agent' && !getUser?.agent?.license) {
        router.push(
          `/signup/agent-details?redirect=${encodeURIComponent(router.asPath)}`,
        );
      }
    },
    onFail: (err) => {
      Sentry.captureException(err);
      // If API call fails, user might have session but invalid token
      // We'll keep them on the pricing page in this case
    },
  });

  // Check for valid Cognito session before calling GET_USER
  const checkCognitoSession = async () => {
    try {
      const session = await Promise.race([
        Auth.currentSession(),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error('Session request timed out')),
            5000,
          ),
        ),
      ]);

      const isValid = session.isValid();
      setHasValidSession(isValid);

      // Only call GET_USER if we have a valid session
      if (isValid) {
        getUserQuery.execute();
      }
    } catch (err) {
      // No valid session, that's okay for this page
      setHasValidSession(false);
    } finally {
      setSessionChecked(true);
    }
  };

  useEffect(() => {
    // Check Cognito session on mount
    checkCognitoSession();

    // Also refresh when returning from Stripe checkout
    const query = new URLSearchParams(window.location.search);
    if (query.get('success') && hasValidSession) {
      getUserQuery.execute();
    }
  }, []); // Empty dependency array means this runs once on mount

  // Check session when returning from another tab or when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkCognitoSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const onFreeSubscribe = () => {
    // Close Intercom if it's open
    if (w.Intercom) {
      w.Intercom('hide');
    }

    if (isLoggedIn) {
      router.push('/my-trusty/properties/');
    } else {
      router.push('/signup');
    }
  };

  const onProSubscribe = () => {
    // Close Intercom if it's open
    if (w.Intercom) {
      w.Intercom('hide');
    }

    if (isLoggedIn && !subscriptionValid) {
      let subscriptionRenewal = 'month';
      let subscriptionType = 'prov2-monthly';

      if (proType === 'yearly') {
        subscriptionRenewal = 'year';
        subscriptionType = 'prov2-yearly';
      }

      createSubscription.execute({
        body: {
          subscriptionRenewal,
          subscriptionType,
        },
      });
    } else {
      if (isLoggedIn && subscriptionValid) {
        router.push('/my-trusty/properties/');
      } else {
        router.push({
          pathname: '/signup',
          query: {
            subscriptionRenewal: proType === 'yearly' ? 'year' : 'month',
            subscriptionType:
              proType === 'yearly' ? 'prov2-yearly' : 'prov2-monthly',
          },
        });
      }
    }
  };

  const handleClickContactSales = () => {
    if (w.Intercom) {
      // Check if Intercom is already visible
      const intercomElement = document.querySelector(
        '.intercom-messenger-frame',
      );
      const intercomLauncher = document.querySelector(
        '.intercom-launcher-frame',
      );

      // Check both display and visibility attributes
      const isIntercomVisible =
        intercomElement &&
        window.getComputedStyle(intercomElement).display !== 'none' &&
        window.getComputedStyle(intercomElement).visibility !== 'hidden' &&
        !intercomElement.classList.contains(
          'intercom-messenger-frame-collapsed',
        );

      if (isIntercomVisible) {
        w.Intercom('hide');
      } else {
        w.Intercom('show');
      }
    }
  };

  return (
    <div>
      <Head path={`/pricing`} />
      <main>
        <TemplateContainer opacity={0.65} absoluteHeader>
          <Styled.Container>
            <Styled.Wrapper>
              <Styled.Title>
                Join top agents discovering opportunities others miss
              </Styled.Title>

              <PricingComponent
                isLoggedIn={isLoggedIn}
                buttonText={isLoggedIn ? 'Select' : undefined}
                handleClickContactSales={handleClickContactSales}
                onFreeSubscribe={onFreeSubscribe}
                onProSubscribe={onProSubscribe}
                subscriptionValid={subscriptionValid}
                loading={createSubscription.loading || !sessionChecked}
                proType={proType}
                setProType={setProType}
                eliteType={eliteType}
                setEliteType={setEliteType}
              />
            </Styled.Wrapper>
            <Styled.TrustedSection>
              <Styled.TrustedTitle>
                Trusted by Agents at Premier Brokerages
              </Styled.TrustedTitle>

              <Styled.BrokerageRow>
                <Styled.BrokerageContainer>
                  <Styled.BrokerageLogos>
                    <Image
                      src={SotherbysLogo}
                      alt="Sotheby's International Realty"
                      height={20}
                      priority
                    />
                  </Styled.BrokerageLogos>
                  <Styled.BrokerageLogos>
                    <Image
                      src={CompassLogo}
                      alt="Compass"
                      height={20}
                      priority
                    />
                  </Styled.BrokerageLogos>
                  <Styled.BrokerageLogos>
                    <Image
                      src={ColdwellLogo}
                      alt="Coldwell Banker"
                      height={20}
                      priority
                    />
                  </Styled.BrokerageLogos>
                  <Styled.BrokerageLogos>
                    <Image src={SideLogo} alt="Side" height={20} priority />
                  </Styled.BrokerageLogos>
                </Styled.BrokerageContainer>
              </Styled.BrokerageRow>
            </Styled.TrustedSection>
          </Styled.Container>
        </TemplateContainer>
      </main>
    </div>
  );
};

export default Pricing;
